import Model, { attr, belongsTo, hasMany } from '@warp-drive/legacy/model';
import type { Store } from '@warp-drive/core';
import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';
import type Intl from 'ember-intl/services/intl';
import type PersonModel from './person.ts';
import type AgentModel from './agent.ts';
import type AnswerProfileModel from './answer-profile.ts';
import type AccountModel from './account.ts';
import type RemoteEventModel from './remote-event.ts';
import type RecommendationModel from './recommendation.ts';
import type EmployeeModel from './employee.ts';
import type RoleViewModel from './role-view.ts';
import type UserRoleModel from './user-role.ts';
import type UserRelationshipModel from './user-relationship.ts';
import type LoginModel from './login.ts';
import type StrategyFinderResultModel from './strategy-finder-result.ts';
import type AgreementModel from './agreement.ts';
import type EmailAddressModel from './email-address.ts';
import type WellnessTrainModel from './wellness-train';
import { Type } from '@warp-drive/core-types/symbols';

export default class UserModel extends Model {
  @service declare intl: Intl;
  @service declare store: Store;

  @attr declare updatedAt: string;
  @attr declare createdAt: string;
  @attr declare isActive: boolean;

  /*************************
   **  Relationships      **
   *************************/

  @belongsTo('person', { async: false, inverse: 'user' })
  declare person: PersonModel;
  @belongsTo('agent', { async: false, inverse: null })
  declare agent: AgentModel;
  @belongsTo('answer-profile', { async: false, inverse: 'user' })
  declare answerProfile: AnswerProfileModel;
  @hasMany('account', { async: false, inverse: null })
  declare accounts: AccountModel[];
  @hasMany('remote-event', { async: false, inverse: null })
  declare remoteEvents: RemoteEventModel[];
  @hasMany('recommendation', { async: false, inverse: null })
  declare recommendations: RecommendationModel[];
  @hasMany('employee', { async: false, inverse: null })
  declare employees: EmployeeModel[];
  @hasMany('user', { async: false, inverse: null })
  declare families: UserModel[];
  @hasMany('role-view', { async: false, inverse: null })
  declare roleViews: RoleViewModel[];
  @hasMany('user-role', { async: false, inverse: 'user' })
  declare userRoles: UserRoleModel[];
  @hasMany('login', { async: false, inverse: null })
  declare logins: LoginModel[];
  @hasMany('strategy-finder-result', { async: false, inverse: null })
  declare strategyFinderResults: StrategyFinderResultModel[];
  @hasMany('agreement', { async: false, inverse: 'user' })
  declare agreements: AgreementModel[];
  @belongsTo('agreement', { async: false, inverse: null })
  declare usageAgreement: AgreementModel;
  @belongsTo('agreement', { async: false, inverse: null })
  declare privacyAgreement: AgreementModel;
  @hasMany('agreement', { async: false, inverse: null })
  declare customRegisterAgreements: AgreementModel;
  @hasMany('email-address', { async: false, inverse: 'user' })
  declare emailAddresses: EmailAddressModel[];
  @belongsTo('wellness-train', { async: false, inverse: 'user' })
  declare wellnessTrain: WellnessTrainModel;

  /**************************
   **  Computed Properties **
   **************************/

  /**
   * Computes role views from userRoles.relationships to avoid slow vw_role view query.
   * Falls back to roleViews for backwards compatibility during migration.
   *
   * Important: The company is obtained from jurisdiction.company (Company has_one relationship
   * via user_role_id), NOT from jurisdiction.user (which is the legacy ORGANIZATION User).
   */
  get computedRoleViews(): RoleViewModel[] {
    if (!this.userRoles || this.userRoles.length === 0) {
      return this.roleViews || [];
    }

    const computed: Partial<RoleViewModel>[] = [];
    this.userRoles.forEach((userRole: UserRoleModel) => {
      if (!userRole.relationships || userRole.relationships.length === 0) {
        // If role has no relationships, still create a role view entry
        // For EMPLOYEE roles without relationships, try to get company from employee
        const company = userRole.employee?.company ?? undefined;
        computed.push({
          id: userRole.id,
          role: userRole.roleType,
          roleType: userRole.roleType,
          relationshipType: userRole.roleType,
          isActive: userRole.isActive,
          company,
        });
      } else {
        userRole.relationships.forEach((relationship: UserRelationshipModel) => {
          // Get company from jurisdiction's company relationship (Company.user_role_id = jurisdiction.id)
          // This is the actual Company record, not the legacy ORGANIZATION User
          const jurisdictionCompany = relationship.jurisdiction?.company;
          computed.push({
            id: relationship.id,
            role: userRole.roleType,
            roleType: userRole.roleType,
            relationshipType: relationship.relationshipType,
            isActive: relationship.isActive,
            company: jurisdictionCompany ?? undefined,
          });
        });
      }
    });

    return computed as RoleViewModel[];
  }

  get roles() {
    return this.computedRoleViews;
  }

  get primaryEmail() {
    return this.emailAddresses?.find((e) => e.contactType === 'PRIMARY');
  }

  get personalEmail() {
    return this.emailAddresses?.find((e) => e.contactType === 'PERSONAL');
  }

  @cached
  get uniqueRolesByRelationshipType() {
    const relationshipMap = new Map();
    this.roles.forEach((role) => {
      const existingRole = relationshipMap.get(role.relationshipType);
      if (!existingRole) {
        // If no role exists for this relationshipType, add it
        relationshipMap.set(role.relationshipType, role);
      } else if (!existingRole.isActive && role.isActive) {
        // If a duplicate exists, prefer the active role
        relationshipMap.set(role.relationshipType, role);
      }
      // If both are active or both are inactive, keep the first one (no action needed)
    });

    const uniqueRoles = Array.from(relationshipMap.values());
    return Object.freeze(uniqueRoles);
  }

  get needsToAnswerWhyNoLinkedLoans() {
    return !!this.person?.needsToAnswerWhyNoLinkedLoans;
  }

  get isTioAdmin() {
    return !!this.roles?.find((role) => {
      return role.relationshipType === 'TIO_ADMIN';
    });
  }

  get isTioDevOps() {
    return this.employees.some((e) => {
      return (
        e.isActive &&
        e.jurisdictiveRelationships.some(
          (jr) => jr.isActive && jr.relationshipType === 'TIO_DEVOPS'
        )
      );
    });
  }

  get isEmployee() {
    return !!this.roles?.find((role) => {
      return role.relationshipType === 'PARTICIPANT';
    });
  }

  get isEmployeeAdmin() {
    return !!this.roles?.find((role) => {
      return role.relationshipType === 'EMPLOYEE_ADMIN';
    });
  }

  get isAccountOwner() {
    return !!this.roles?.find((role) => {
      return role.relationshipType === 'ACCOUNT_OWNER';
    });
  }

  get isAccountOwnerOrEmployeeAdmin() {
    return this.isAccountOwner || this.isEmployeeAdmin;
  }

  get isAgent() {
    return !!this.roles?.find((role) => {
      return role.role === 'WORKSPACE_AGENT';
    });
  }

  get isFamily() {
    return !!this.roles?.find((role) => {
      return role.role === 'FAMILY_MEMBER';
    });
  }

  get isPslfAdmin() {
    return !!this.roles?.find((role) => {
      return role.relationshipType === 'PSLF_ADMIN';
    });
  }

  get isTasTioApprover() {
    return !!this.roles?.find((role) => {
      return role.relationshipType === 'TAS.Approver.TIO_APPROVER';
    });
  }

  get isTasAdmin() {
    return !!this.roles?.find((role) => {
      return role.relationshipType.startsWith('TAS.Admin');
    });
  }

  get isTasProgramAdmin() {
    return !!this.roles?.find((role) => {
      return role.relationshipType === 'TAS.Admin.PROGRAM_ADMIN';
    });
  }

  get isTasApprover() {
    return !!this.roles?.find((role) => {
      return role.relationshipType.startsWith('TAS.Approver');
    });
  }

  get isTasSupervisor() {
    return !!this.roles?.find((role) => {
      return role.relationshipType === 'TAS.Approver.SUPERVISOR';
    });
  }

  get isPayOps() {
    return !!this.roles?.find((role) => {
      return role.relationshipType === 'TIO_PAYMENT_OPS';
    });
  }

  get canEditTasApplication() {
    const approvedRoles = [
      'TAS.Admin.PROGRAM_ADMIN',
      'TAS.Admin.SUPERUSER',
      'TAS.Approver.ADMIN_APPROVER',
      'TAS.Approver.APPROVER',
      'TAS.Approver.CUSTOM_APPROVER',
      'TAS.Approver.FINANCE_APPROVER',
      'TAS.Approver.HYPERVISOR',
      'TAS.Approver.PPP_FINANCE_APPROVER',
      'TAS.Approver.TAP_FINANCE_APPROVER',
      'TAS.Approver.TIO_APPROVER',
      'TAS.Approver.SUPERVISOR',
      'TIO_ACCOUNT_MANAGER',
      'TIO_ADMIN',
      'EMPLOYEE_ADMIN',
      'TIO_PAYMENT_OPS',
    ];
    return !!this.roles?.find((role) => {
      return approvedRoles.includes(role.relationshipType);
    });
  }

  get isEnrolled() {
    return !!this.logins?.find((login) => login.isActive);
  }

  /**
   * Queries the API for aggregated data that this user has access to.
   *
   * The query can contain one or more of the following properties:
   *
   * `model`
   * : The model whose data is being aggregated
   *
   * `groupBy`
   * : Which property of the model should be used to group the data before aggregation
   *
   * `rollups`
   * : An object mapping field names to the formulas used to calculate the rollup values for those
   *   fields.
   *
   * The return value will be an object in the form:
   *
   * ```
   * {
   *   data: {
   *     name?: string;       // the value of the `groupBy`` property for this group
   *     ...rollups?: any;    // the computed rollup values for this group
   *   }
   * }
   * ````
   */
  async aggregate(query: Record<string, unknown>) {
    return this.store.adapterFor('user').aggregate(this, query);
  }

  async deactivateUser(userId: string) {
    return this.store.adapterFor('user').deactivateUser(userId);
  }

  [Type] = 'user' as const;
}
